// NotificationBell — in-app notification bell with shared state across mounts,
// desktop dropdown / mobile sheet, polling, toast on new arrival, vibrate.
import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Bell, Inbox, X, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { apiUrl } from "../lib/api";

const POLL_INTERVAL_MS = 30000;

function timeAgoDe(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const sec = Math.max(1, Math.floor((now - then) / 1000));
  if (sec < 60) return `vor ${sec} Sek.`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `vor ${min} Minute${min === 1 ? "" : "n"}`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `vor ${hr} Stunde${hr === 1 ? "" : "n"}`;
  const days = Math.floor(hr / 24);
  return `vor ${days} Tag${days === 1 ? "" : "en"}`;
}

function tryVibrate(ms = 50) {
  try {
    if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(ms);
  } catch (_) {
    /* ignore */
  }
}

// ---------- Shared state (so multiple bell mounts stay in sync) ----------
const NotificationContext = createContext(null);

export function NotificationsProvider({ children, token, role }) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const lastSeenIdsRef = useRef(new Set());
  const firstFetchRef = useRef(true);

  const fetchNotifications = useCallback(async () => {
    if (!token) {
      setItems([]);
      setUnread(0);
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(apiUrl("/notifications"), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = res.data || {};
      const list = data.notifications || [];
      const newUnread = data.unread_count ?? list.filter((n) => !n.is_read).length;

      if (!firstFetchRef.current) {
        const newOnes = list.filter((n) => !lastSeenIdsRef.current.has(n.id));
        if (newOnes.length > 0) {
          tryVibrate(80);
          newOnes.slice(0, 3).forEach((n) => {
            const isWorker = role === "worker" || role === "provider";
            const titleText =
              n.type === "new_request" && isWorker ? "Neue Anfrage erhalten" : n.title;
            toast(titleText, { description: n.message, duration: 5000 });
          });
        }
      }
      lastSeenIdsRef.current = new Set(list.map((n) => n.id));
      firstFetchRef.current = false;

      setItems(list);
      setUnread(newUnread);
    } catch (_) {
      /* polling errors silenced */
    } finally {
      setLoading(false);
    }
  }, [token, role]);

  useEffect(() => {
    if (!token) {
      setItems([]);
      setUnread(0);
      setLoading(false);
      firstFetchRef.current = true;
      return undefined;
    }
    setLoading(true);
    firstFetchRef.current = true;
    fetchNotifications();
    const id = setInterval(fetchNotifications, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [token, fetchNotifications]);

  const markAllRead = useCallback(async () => {
    if (!token) return;
    try {
      await axios.post(
        apiUrl("/notifications/mark-read"),
        { mark_all: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnread(0);
    } catch (_) { /* ignore */ }
  }, [token]);

  const markOneRead = useCallback(async (id) => {
    if (!token) return;
    try {
      await axios.post(
        apiUrl("/notifications/mark-read"),
        { notification_id: id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
      setUnread((u) => Math.max(0, u - 1));
    } catch (_) { /* ignore */ }
  }, [token]);

  const value = {
    items,
    unread,
    loading,
    role,
    token,
    refresh: fetchNotifications,
    markAllRead,
    markOneRead,
  };
  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    return {
      items: [],
      unread: 0,
      loading: false,
      role: null,
      token: null,
      refresh: () => {},
      markAllRead: () => {},
      markOneRead: () => {},
    };
  }
  return ctx;
}

// ---------- The Bell button + panel ----------
export default function NotificationBell() {
  const { token, role, items, unread, loading, refresh, markAllRead, markOneRead } =
    useNotifications();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  if (!token) return null;

  const handleNotifClick = (n) => {
    if (!n.is_read) markOneRead(n.id);

    // Route based on the *notification's* role (the recipient's role) — not on
    // any external prop — to avoid mismatches when localStorage role is missing.
    const targetIsWorker = n.role === "worker" || n.role === "provider";
    if (n.type === "new_request" && targetIsWorker) {
      navigate("/handwerker");
    } else if (targetIsWorker) {
      navigate("/handwerker");
    } else {
      navigate("/customer");
    }
    setOpen(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 rounded-full hover:bg-gray-100 transition-colors"
        data-testid="notification-bell"
        aria-label="Benachrichtigungen"
      >
        <Bell className="h-5 w-5 text-gray-700" />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center shadow"
            data-testid="notification-unread-count"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div
            className="fixed inset-0 bg-black/30 z-40 md:hidden"
            onClick={() => setOpen(false)}
            data-testid="notification-backdrop"
          />

          <div
            className="hidden md:block absolute right-4 top-14 w-96 bg-white rounded-xl shadow-2xl border border-gray-100 z-50 overflow-hidden animate-[fadeIn_.15s_ease-out]"
            data-testid="notification-panel-desktop"
          >
            <NotificationPanel
              items={items}
              loading={loading}
              unread={unread}
              onClose={() => setOpen(false)}
              onMarkAll={markAllRead}
              onClickNotif={handleNotifClick}
            />
          </div>

          <div
            className="md:hidden fixed inset-x-0 bottom-0 top-16 bg-white z-50 rounded-t-2xl shadow-2xl flex flex-col animate-[slideUp_.2s_ease-out]"
            data-testid="notification-panel-mobile"
          >
            <NotificationPanel
              items={items}
              loading={loading}
              unread={unread}
              onClose={() => setOpen(false)}
              onMarkAll={markAllRead}
              onClickNotif={handleNotifClick}
              fullHeight
            />
          </div>
        </>
      )}

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(-4px) } to { opacity: 1; transform: translateY(0) } }
        @keyframes slideUp { from { transform: translateY(100%) } to { transform: translateY(0) } }
      `}</style>
    </>
  );
}

function NotificationPanel({ items, loading, unread, onClose, onMarkAll, onClickNotif, fullHeight }) {
  return (
    <div className={`flex flex-col ${fullHeight ? "h-full" : "max-h-[70vh]"}`}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <Bell className="h-4 w-4 text-gray-700" />
          <h3 className="font-semibold text-gray-900">Benachrichtigungen</h3>
          {unread > 0 && (
            <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded-full font-medium">
              {unread} ungelesen
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {unread > 0 && (
            <button
              onClick={onMarkAll}
              className="text-xs text-blue-600 hover:underline px-2 py-1"
              data-testid="notification-mark-all-read"
            >
              Alle gelesen
            </button>
          )}
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100" aria-label="Schließen">
            <X className="h-4 w-4 text-gray-500" />
          </button>
        </div>
      </div>

      <div className="overflow-y-auto flex-1">
        {loading && items.length === 0 ? (
          <NotificationSkeleton />
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-gray-500" data-testid="notification-empty">
            <Inbox className="h-10 w-10 mx-auto mb-3 text-gray-300" />
            <p className="text-sm">Keine Benachrichtigungen</p>
            <p className="text-xs mt-1 text-gray-400">Neue Anfragen erscheinen hier automatisch.</p>
          </div>
        ) : (
          <ul data-testid="notification-list">
            {items.map((n) => (
              <li
                key={n.id}
                onClick={() => onClickNotif(n)}
                className={`px-4 py-3 border-b border-gray-50 cursor-pointer transition-colors hover:bg-gray-50 ${
                  !n.is_read ? "bg-blue-50/40" : ""
                }`}
                data-testid={`notification-item-${n.id}`}
              >
                <div className="flex items-start gap-3">
                  <NotificationIcon type={n.type} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className={`text-sm ${!n.is_read ? "font-semibold text-gray-900" : "font-medium text-gray-700"}`}>
                        {n.title}
                      </p>
                      {!n.is_read && (
                        <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" />
                      )}
                    </div>
                    <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">{n.message}</p>
                    <p className="text-[11px] text-gray-400 mt-1">{timeAgoDe(n.created_at)}</p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function NotificationIcon({ type }) {
  const wrap = "w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0";
  if (type === "new_request") {
    return (
      <div className={`${wrap} bg-emerald-100`}>
        <Bell className="h-4 w-4 text-emerald-600" />
      </div>
    );
  }
  if (type === "request_accepted") {
    return (
      <div className={`${wrap} bg-blue-100`}>
        <CheckCircle className="h-4 w-4 text-blue-600" />
      </div>
    );
  }
  if (type === "new_message") {
    return (
      <div className={`${wrap} bg-violet-100`}>
        <Inbox className="h-4 w-4 text-violet-600" />
      </div>
    );
  }
  return (
    <div className={`${wrap} bg-gray-100`}>
      <Bell className="h-4 w-4 text-gray-500" />
    </div>
  );
}

function NotificationSkeleton() {
  return (
    <ul className="divide-y divide-gray-50" data-testid="notification-skeleton">
      {[1, 2, 3, 4].map((i) => (
        <li key={i} className="p-4">
          <div className="flex items-start gap-3 animate-pulse">
            <div className="w-9 h-9 rounded-full bg-gray-200" />
            <div className="flex-1 space-y-2">
              <div className="h-3 bg-gray-200 rounded w-2/3" />
              <div className="h-2.5 bg-gray-100 rounded w-full" />
              <div className="h-2 bg-gray-100 rounded w-1/3" />
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}
